import React from 'react';
import PropTypes from 'prop-types';
import '../../styles/App.css';
import '../../styles/intercom.css';
import ToolsContainer from '../../containers/tools/ToolsContainer';
import ConversationContainer from '../../containers/ConversationContainer';
import InboxContainer from '../../containers/inboxPanel/InboxContainer';
import * as SessionAnalyticsService from 'services/SessionAnalyticsService/SessionAnalyticsService';
import { getLoggedInExpert } from 'api/expert';
import { initializeListener, removeListener } from '../../util/activity';
import { initializeVisibilityListener, removeVisibilityListener } from '../../util/screenListeners';
import * as ErrorService from 'services/ErrorService/ErrorService';
import environment from '../../environment';
import { trackIntercomOpenEvent } from '../../analytics/screenEventAnalytics';
import TitleNotification from '../../util/TitleNotification';
import { AUTO_UNAVAILABLE, AVAILABLE } from '../../constants/expertAvailability';
import { showNotification } from '../../util';
import Nudge from '../Nudge/Nudge';
import CenterInfoPanel from 'components/CenterInfoPanel/CenterInfoPanel';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { Section, SplitPages, BoxHolder } from './ExpertPortalComponent.styles';
import { encryptUser } from 'util/intercomUtil';
import * as tabs from '../../constants/tabNames';
import { hasFilterParams } from '../../util/filterUrlSerializer';

const INITIAL_SECTION_SIZE_TOOLS_OPENED = [0, 50, 60];
const INITIAL_SECTION_SIZE_TOOLS_CLOSED = [50, 60, 0];

class ExpertPortal extends React.Component {
  constructor(props) {
    super(props);
    const sectionSizes = this.props.general.showTools
      ? INITIAL_SECTION_SIZE_TOOLS_OPENED
      : INITIAL_SECTION_SIZE_TOOLS_CLOSED;

    this.state = {
      open: false,
      user: {},
      sectionSizes: sectionSizes,
    };
    this.titleNotification = null;

    getLoggedInExpert().then((simplrExpert) => {
      if (!isEmpty(simplrExpert)) {
        ErrorService.configure((scope) => {
          scope.setUser({
            id: simplrExpert.id,
            name: simplrExpert.name,
            email: simplrExpert.email,
            username: simplrExpert.username,
            user_hash: encryptUser(simplrExpert.id),
          });
          scope.setExtra('isAdmin', simplrExpert.isAdmin);
        });
      }
    });
  }

  UNSAFE_componentWillMount() {
    this.props.loading(true, 'inbox');
  }

  async componentDidMount() {
    // A gist for the original reference for the below can be found at the following: https://gist.github.com/sn3p/852cfed0ee22b07b5263ca04930705c8
    // Additional information for reference may be available at https://en.defacto.nl/blog/customizing-the-intercom-launcher/
    const launcher = document.querySelector('.intercom-launcher');
    const unreadCount = launcher.querySelector('.intercom-unread-count');

    // Initialize Intercom
    window.Intercom('boot', {
      app_id: environment.intercom.intercomId,
      custom_launcher_selector: '.intercom-launcher',
      hide_default_launcher: true,
    });

    // Register callbacks
    window.Intercom('onShow', () => launcher.classList.add('intercom-open'));

    window.Intercom('onHide', () => launcher.classList.remove('intercom-open'));

    window.Intercom('onUnreadCountChange', (count) => {
      unreadCount.textContent = count;
      if (count) {
        unreadCount.classList.add('active');
      } else {
        unreadCount.classList.remove('active');
      }
    });

    getLoggedInExpert().then((simplrExpert) => {
      if (simplrExpert) {
        /** NOTE(apoorvyadav1|SPD-13362|2025-10-15):
         * Previously, FullStory was initialized when the website loaded, which is not ideal for privacy compliance.
         * Initialize FullStory if user is logged in and it's not already initialized
         */
        SessionAnalyticsService.initializeSessionAnalyticsServices();

        SessionAnalyticsService.identify(simplrExpert.id, {
          name: simplrExpert.name,
          email: simplrExpert.email,
          specialist: simplrExpert.isAdmin || false,
        });

        window.Intercom('onShow', () => {
          trackIntercomOpenEvent();
          launcher.classList.add('intercom-open');
        });
        window.Intercom('update', {
          app_id: environment.intercom.intercomId,
          name: simplrExpert.name,
          email: simplrExpert.email,
          user_id: simplrExpert.id,
          user_hash: encryptUser(simplrExpert.id),
        });
      }
    });

    // Wait for Intercom to boot (max 30 seconds)
    const timeout = setTimeout(() => clearInterval(interval), 30000);
    const interval = setInterval(() => {
      if (window.Intercom.booted) {
        // Add class to show the launcher
        launcher.classList.add('intercom-booted');

        clearInterval(interval);
        clearTimeout(timeout);
      }
    }, 100);

    initializeVisibilityListener();

    if (!this.props.isAdmin) {
      this.activityListener = initializeListener(this);
    }

    // if user queries for a specific ticket, all tickets won't get loaded
    const query = new URLSearchParams(this.props.location.search);
    const queryTicketNumber = query.get('ticket');

    // NOTE(vlad|SPD-14347|2026-03-04): Deep-link / shared URL edge case — switch to ALL tab so URL filter params are applied (other tabs ignore them)
    const shouldSwitchToAllTab =
      (queryTicketNumber || hasFilterParams(this.props.location.search)) && this.props.selectedInboxTab !== tabs.ALL;

    if (shouldSwitchToAllTab) {
      this.props.selectInboxTabWithoutLoadingTickets(tabs.ALL);
    }

    await this.props.onLoad(this.props.liveQueryStarted, queryTicketNumber);

    SessionAnalyticsService.event('Expert Initial Tickets', {
      nominated: this.props.tickets.nominatedTickets.allTickets.length,
      assignedTickets: this.props.tickets.assignedTickets.allTickets.length,
    });

    this.props.loading(false, '');

    if (!this.props.isAdmin) {
      window.addEventListener('copy', this.saveCopiedText);
    }

    // NOTE(vlad|SPD-14305|2026-02-26): Detect bfcache page restore (e.g., browser back from external Admin UI)
    // and re-fetch tickets since WebSocket/subscription connections may have died
    this.handlePageShow = async (event) => {
      if (event.persisted) {
        this.props.loading(true, 'inbox');
        await this.props.refreshTickets();
        this.props.loading(false, '');
      }
    };
    window.addEventListener('pageshow', this.handlePageShow);
  }

  componentWillUnmount() {
    removeVisibilityListener();

    if (this.activityListener) {
      // make sure we don't get listeners running for different users
      removeListener(this.activityListener);
    }
    if (this.titleNotification !== null) {
      this.titleNotification.clear();
    }
    window.removeEventListener('copy', this.saveCopiedText);
    window.removeEventListener('pageshow', this.handlePageShow);
  }

  saveCopiedText = () => {
    const selection = window.getSelection();
    const copiedText = selection && selection.toString();
    if (copiedText) {
      this.props.setClipboardText(copiedText);
    }
  };

  componentDidUpdate(prevProps) {
    const unreadNominatedTicketIds = this.props.unreadNominatedTicketIds;

    if (this.props.unreadNominatedTicketIds !== prevProps.unreadNominatedTicketIds) {
      if (this.titleNotification !== null) {
        this.titleNotification.clear();
      }
      if (unreadNominatedTicketIds.length > 0) {
        const notificationTitle = `${unreadNominatedTicketIds.length} incoming ticket${
          unreadNominatedTicketIds.length > 1 ? 's' : ''
        }`;
        this.titleNotification = new TitleNotification(notificationTitle);
      }
    }

    if (!this.props.isAdmin && prevProps.availability === AVAILABLE && this.props.availability === AUTO_UNAVAILABLE) {
      showNotification('You are unavailable', 'Check back in to continue to receive tickets');
    }

    const prevShowTools = get(prevProps, 'general.showTools', null);
    const currShowTools = get(this.props, 'general.showTools', null);

    // reorder section sizes on tools open
    if (prevShowTools !== currShowTools) {
      this.setState((prevState) => ({
        sectionSizes: this.reorderSectionsSizes(prevState.sectionSizes, currShowTools),
      }));
    }
  }

  // reorders sections sizes to hide a section depending on showTools
  reorderSectionsSizes = (sizes, showTools) => {
    if (!Array.isArray(sizes) || sizes.length !== 3) {
      return sizes;
    }

    return showTools ? [sizes[2], sizes[0], sizes[1]] : [sizes[1], sizes[2], sizes[0]];
  };

  // updates sectionSizes values with 200 delay to prevent multiple setState calls
  onDrag = debounce((sectionSizes) => {
    this.setState({ sectionSizes });
  }, 200);

  renderCenterPanelAndTools = () => (
    <>
      <CenterInfoPanel />
      <ToolsContainer />
    </>
  );

  render() {
    const { general, isExpertDashboardOpened, isCommerceWizardActive } = this.props;

    return (
      <BoxHolder>
        <SplitPages
          sizes={this.state.sectionSizes}
          gutterSize={4}
          minSize={0}
          gutterAlign="center"
          snapOffset={0}
          dragInterval={1}
          direction="horizontal"
          cursor="col-resize"
          $showtools={general.showTools}
          onDrag={this.onDrag}
          $hidegutter={isExpertDashboardOpened}
        >
          <Section hide={general.showTools} hideGutter={isExpertDashboardOpened || isCommerceWizardActive}>
            {!general.showTools && <InboxContainer />}
          </Section>
          <Section hidegutter={isExpertDashboardOpened}>
            {!isExpertDashboardOpened && <ConversationContainer />}
          </Section>
          <Section hide={!general.showTools} hidegutter={isExpertDashboardOpened}>
            {general.showTools && this.renderCenterPanelAndTools()}
          </Section>
        </SplitPages>
        <Nudge />
      </BoxHolder>
    );
  }
}

ExpertPortal.propTypes = {
  general: PropTypes.shape({
    showTools: PropTypes.bool,
  }).isRequired,
  messages: PropTypes.arrayOf(PropTypes.object),
};

export default ExpertPortal;
